/**
 * Data Connections Page
 *
 * Manage external data source connections with a clean, modern interface.
 * Follows the same design patterns as Search Results and Talent Configuration pages.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { ConnectionsListView } from '../../components/data-connections/ConnectionsListView';
import { CreateConnectionModal } from '../../components/data-connections/CreateConnectionModal';
import { ConnectionDetailPane } from '../../components/data-connections/ConnectionDetailPane';
import { type ConnectorConfiguration } from '../../generated/connectors/connectors';
import { useGetAvailableConnectorTypesApiConnectorsTypesGet } from '../../generated/data-connectors/data-connectors';
import { ReadableDropdown, type ReadableDropdownOption } from '../../components/common/ReadableDropdown';
import { getConnectorLabel } from '../../components/data-connections/connectorCatalog';
import { Badge, Button } from '../../components/ui';

interface NewConnectionMenuProps {
  onSelect: (type: string) => void;
}

function NewConnectionMenu({ onSelect }: NewConnectionMenuProps) {
  const { data: connectorTypesData, isLoading } = useGetAvailableConnectorTypesApiConnectorsTypesGet();
  const allConnectors = connectorTypesData?.connector_types || [];

  // Only show available connectors (hide "coming soon" ones)
  const availableConnectors = allConnectors.filter(c => c.available);

  const dropdownOptions: ReadableDropdownOption[] = availableConnectors.map((connector) => ({
    key: connector.type,
    label: connector.name,
    description: connector.description,
    onClick: () => onSelect(connector.type),
  }));

  return (
    <ReadableDropdown
      trigger={
        <Button disabled={isLoading}>
          <PlusIcon className="h-4 w-4" />
          New Source Connection
          <ChevronDownIcon className="h-3.5 w-3.5" />
        </Button>
      }
      options={dropdownOptions}
      align="right"
      disabled={isLoading}
    />
  );
}

export function DataConnectionsPage() {
  const [searchParams] = useSearchParams();
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createConnectorType, setCreateConnectorType] = useState<string>('people_data_labs');
  const filteredSourceType = searchParams.get('type') || undefined;
  const rawCategory = searchParams.get('category');
  const categoryFilterInitial =
    rawCategory === 'warehouse' ||
      rawCategory === 'catalog' ||
      rawCategory === 'apps' ||
      rawCategory === 'documents'
      ? rawCategory
      : undefined;
  const shouldOpenCreate = searchParams.get('new') === '1';
  const initialCreateHandled = useRef(false);

  const openCreateModal = (type: string = 'people_data_labs') => {
    setCreateConnectorType(type);
    setShowCreateModal(true);
  };

  const handleSelectConnection = (connection: ConnectorConfiguration | null) => {
    setSelectedConnectionId(connection ? connection.connector_id : null);
  };

  const handleConnectorDeleted = () => {
    setSelectedConnectionId(null);
  };

  useEffect(() => {
    if (!shouldOpenCreate || initialCreateHandled.current) {
      return;
    }
    initialCreateHandled.current = true;
    openCreateModal(filteredSourceType || 'people_data_labs');
  }, [filteredSourceType, shouldOpenCreate]);

  const pageDescription = 'Create and maintain tenant-wide connector instances to bring external data INTO the platform for use by workspaces, applets, and live retrieval flows.';

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50 dark:bg-dark-bg">
      {/* Page Header */}
      <div
        className="flex-shrink-0 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface"
        data-toolbar-surface="solid"
      >
        <div className="px-6 py-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                Admin settings
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <h1 className="text-lg font-semibold text-charcoal dark:text-gray-100">Data Connections</h1>
                <Badge variant="secondary">Tenant-wide</Badge>
                {filteredSourceType && (
                  <Badge variant="outline">{getConnectorLabel(filteredSourceType)}</Badge>
                )}
              </div>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                {pageDescription}
              </p>

            </div>
            <div className="flex flex-wrap items-center gap-2">
              <NewConnectionMenu onSelect={openCreateModal} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Split Pane Layout */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Panel - Connections List */}
        <div className="w-[360px] flex-shrink-0 border-r border-gray-200 dark:border-dark-border overflow-hidden flex flex-col bg-white dark:bg-dark-surface">
          <ConnectionsListView
            className="flex-1 min-h-0"
            onCreateNew={() => openCreateModal(filteredSourceType || 'people_data_labs')}
            onSelectConnection={handleSelectConnection}
            selectedConnectionId={selectedConnectionId}
            defaultTypeFilter={filteredSourceType}
            initialCategoryFilter={categoryFilterInitial}
          />
        </div>

        {/* Right Panel - Connection Details */}
        <div className="flex-1 min-h-0 overflow-hidden bg-white dark:bg-dark-surface">
          <ConnectionDetailPane
            connectorId={selectedConnectionId}
            onDeleted={handleConnectorDeleted}
          />
        </div>
      </div>

      {/* Create Connection Modal */}
      <CreateConnectionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        defaultConnectorType={createConnectorType}
      />
    </div>
  );
}

export default DataConnectionsPage;
